// NetworkingDetailsModal.jsx
import React, { useEffect, useState, useCallback, memo } from "react";
import { X, Search, Gift, Users } from "lucide-react";

const ROLES = [
  {
    value: "Seeker",
    label: "Seeker",
    description: "I'm looking for advice or help",
    Icon: Search,
    accentClass: "seeker",
  },
  {
    value: "Giver",
    label: "Giver",
    description: "I want to share my expertise",
    Icon: Gift,
    accentClass: "giver",
  },
  {
    value: "Both",
    label: "Both",
    description: "I want to give and receive equally",
    Icon: Users,
    accentClass: "both",
  },
];

const NetworkingDetailsModal = memo(function NetworkingDetailsModal({
  open,
  initial,
  onCancel,
  onSubmit,
  tableName,
}) {
  const [role, setRole] = useState("");
  const [intent, setIntent] = useState("");

  // Reset internal state every time the modal opens
  useEffect(() => {
    if (!open) return;

    setRole(
      initial?.networkingRole && initial.networkingRole !== "none"
        ? initial.networkingRole
        : "",
    );
    setIntent(initial?.networkingIntent || "");
  }, [open, initial?.networkingRole, initial?.networkingIntent]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onCancel]);

  const isIntentValid = intent.trim().length > 0;
  const canSubmit = !!role && isIntentValid;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    onSubmit?.({
      networkingRole: role,
      networkingIntent: intent.trim(),
    });
  }, [canSubmit, role, intent, onSubmit]);

  const handleSkip = useCallback(() => {
    onSubmit?.({
      networkingRole: role || "none",
      networkingIntent: intent.trim() || "No intent provided",
    });
  }, [role, intent, onSubmit]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onCancel}
      data-testid="networking-details-overlay"
    >
      <div
        className="relative w-[min(96vw,520px)] max-h-[90vh] overflow-y-auto rounded-3xl border border-[#C6A87C]/30 bg-[#0A1D1A] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Join discussion"
        data-testid="networking-details-modal"
      >
        {/* ── Header ── */}
        <div className="flex items-start justify-between px-7 pt-7 pb-4">
          <div className="min-w-0">
            <h3 className="font-display text-xl tracking-tight text-white">
              Join Discussion
            </h3>
            {tableName && (
              <p className="mt-1 text-sm text-[#C6A87C] truncate max-w-[28ch]">
                {tableName}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-white/50 hover:text-white p-1.5 rounded-full hover:bg-white/[0.06] transition-colors shrink-0"
            aria-label="Close"
            data-testid="networking-details-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mx-7 h-px bg-white/[0.08]" />

        {/* ── Role selection ── */}
        <div className="px-7 pt-6 pb-2">
          <p className="text-sm font-medium text-white/90 mb-4">
            What is your role today?
          </p>

          {/* Seeker + Giver row */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            {ROLES.filter((r) => r.value !== "Both").map((r) => {
              const isSelected = role === r.value;
              return (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRole(r.value)}
                  data-testid={`role-${r.value.toLowerCase()}`}
                  className={`
                    relative flex flex-col items-center gap-2.5 p-5 rounded-2xl border text-center
                    transition-all duration-200 cursor-pointer group/role
                    ${
                      isSelected
                        ? "border-[#C6A87C] bg-[#C6A87C]/[0.08] shadow-[0_0_24px_-6px_rgba(123,200,166,0.25)]"
                        : "border-white/[0.1] bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]"
                    }
                  `}
                >
                  {isSelected && (
                    <span className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-[#C6A87C] flex items-center justify-center text-[#061412] text-[11px] font-bold">
                      ✓
                    </span>
                  )}
                  <div
                    className={`
                      w-12 h-12 rounded-full flex items-center justify-center
                      ${
                        isSelected
                          ? "bg-[#C6A87C]/20 text-[#C6A87C]"
                          : "bg-white/[0.06] text-white/50 group-hover/role:text-white/70"
                      }
                    `}
                  >
                    <r.Icon size={22} />
                  </div>
                  <span
                    className={`font-semibold text-sm ${isSelected ? "text-[#C6A87C]" : "text-white/80"}`}
                  >
                    {r.label}
                  </span>
                  <span className="text-[11px] text-white/45 leading-snug">
                    {r.description}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Both — full width row */}
          {(() => {
            const bothRole = ROLES.find((r) => r.value === "Both");
            if (!bothRole) return null;
            const isSelected = role === "Both";

            return (
              <button
                type="button"
                onClick={() => setRole("Both")}
                data-testid="role-both"
                className={`
                  relative w-full flex items-center gap-4 p-4 rounded-2xl border
                  transition-all duration-200 cursor-pointer group/role
                  ${
                    isSelected
                      ? "border-[#C6A87C] bg-[#C6A87C]/[0.08] shadow-[0_0_24px_-6px_rgba(123,200,166,0.25)]"
                      : "border-white/[0.1] bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]"
                  }
                `}
              >
                {isSelected && (
                  <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#C6A87C] flex items-center justify-center text-[#061412] text-[11px] font-bold">
                    ✓
                  </span>
                )}
                <div
                  className={`
                    w-11 h-11 rounded-full flex items-center justify-center shrink-0
                    ${
                      isSelected
                        ? "bg-[#C6A87C]/20 text-[#C6A87C]"
                        : "bg-white/[0.06] text-white/50 group-hover/role:text-white/70"
                    }
                  `}
                >
                  <bothRole.Icon size={20} />
                </div>
                <div className="text-left min-w-0">
                  <span
                    className={`font-semibold text-sm block ${isSelected ? "text-[#C6A87C]" : "text-white/80"}`}
                  >
                    {bothRole.label}
                  </span>
                  <span className="text-[11px] text-white/45 leading-snug">
                    {bothRole.description}
                  </span>
                </div>
              </button>
            );
          })()}

          {!role && (
            <p className="mt-3 text-xs text-red-400/80">
              Please select at least one networking role
            </p>
          )}
        </div>

        {/* ── Intent ── */}
        <div className="px-7 pt-4 pb-2">
          <p className="text-sm font-medium text-white/90 mb-3">
            What would you like to discuss?
          </p>

          <textarea
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
            placeholder='e.g. "I want corporate coaching" or "Sharing tips on scaling sales"'
            rows={4}
            className="w-full bg-white/[0.04] border border-white/[0.12] rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#C6A87C]/50 focus:outline-none focus:ring-1 focus:ring-[#C6A87C]/20 resize-none transition-colors"
            data-testid="networking-intent-input"
          />

          <p className="mt-2 text-[11px] text-white/35">
            e.g. "I want corporate coaching" or "Sharing tips on scaling sales"
          </p>
        </div>

        {/* ── Actions ── */}
        <div className="px-7 pt-4 pb-7 flex items-center gap-3">
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            data-testid="networking-submit"
            className={`
              flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all duration-200
              ${
                canSubmit
                  ? "bg-[#C6A87C] text-[#061412] hover:bg-[#D9BC93] shadow-lg shadow-[#C6A87C]/20"
                  : "bg-white/[0.06] text-white/25 cursor-not-allowed"
              }
            `}
          >
            Submit&nbsp;&nbsp;→
          </button>

          <button
            type="button"
            onClick={handleSkip}
            data-testid="networking-skip"
            className="px-5 py-3 rounded-full text-sm text-white/50 hover:text-white/80 hover:bg-white/[0.04] transition-colors"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
});

export default NetworkingDetailsModal;
